import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Camera, Upload, ArrowLeft, Sparkles, Crown } from "lucide-react";
import HelpButton from "../components/HelpButton";

export default function SnapReceipt() {
  const fileRef = useRef();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [quota, setQuota] = useState(null); // {used, cap, remaining}

  const loadQuota = async () => {
    try {
      const { data } = await api.get("/ocr/quota");
      setQuota(data);
    } catch (e) {
      // silent — endpoint may not exist yet on older backends
    }
  };
  useEffect(() => {
    loadQuota();
  }, []);

  const toBase64 = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });

  const compress = async (dataUrl, maxW = 1400) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = dataUrl;
    });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const raw = await toBase64(f);
      const compressed = await compress(raw);
      setPreview(compressed);

      const { data } = await api.post("/ocr/receipt", { image_base64: compressed });
      loadQuota(); // refresh remaining

      // Auto-save if we got a usable amount. Otherwise fall back to the manual
      // form so the user can fill in what OCR missed. Saves an entire screen
      // for the 80% case where the OCR nails it.
      const amt = parseFloat(data.amount);
      const canAutoSave = Number.isFinite(amt) && amt > 0;

      if (canAutoSave) {
        const payload = {
          date: data.date || new Date().toISOString().slice(0, 10),
          payee: data.payee || "Receipt",
          amount: amt,
          type: "withdrawal",
          category: data.category || "Other",
          memo: "",
          cleared: false,
          receipt_image: compressed,
        };
        try {
          const { data: saved } = await api.post("/transactions", payload);
          sessionStorage.removeItem("pending_receipt");
          // "Saved: $6.50 Starbucks · Edit?" — sonner toast with an Edit action
          // that opens the fresh transaction in the full form.
          toast.success(
            `Saved: -$${amt.toFixed(2)} ${payload.payee}`,
            {
              duration: 6000,
              action: {
                label: "Edit",
                onClick: () => nav(`/transactions/${saved.id}/edit`),
              },
            }
          );
          nav("/");
          return;
        } catch (autoErr) {
          // Fall through to manual form if auto-save 4xx/5xx (quota, plan, etc.)
          toast.warning("Couldn't auto-save — check the details");
        }
      }

      // Manual-fill fallback: stash extracted values in query params + preview
      sessionStorage.setItem("pending_receipt", compressed);
      const params = new URLSearchParams();
      if (data.payee) params.set("payee", data.payee);
      if (data.amount) params.set("amount", String(data.amount));
      if (data.date) params.set("date", data.date);
      if (data.category) params.set("category", data.category);
      params.set("type", "withdrawal");
      params.set("hasReceipt", "1");
      toast.success("Receipt scanned — confirm the details");
      nav(`/add?${params.toString()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="snap-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Snap receipt</h1>
        <HelpButton id="snap" />
      </div>

      {quota && quota.cap !== null && (
        <div
          className={`nb-card p-3 flex items-center justify-between gap-3 ${
            quota.remaining <= 3 ? "!bg-[#fdecc8]" : ""
          }`}
          data-testid="ocr-quota-badge"
        >
          <div className="min-w-0">
            <div className="font-display font-black text-sm">
              {quota.remaining > 0
                ? `${quota.remaining} free receipt scan${quota.remaining === 1 ? "" : "s"} left`
                : "Free scan limit reached this month"}
            </div>
            <div className="text-[11px] font-medium text-[#4a4a4a] mt-0.5">
              {quota.used}/{quota.cap} used this month · resets on the 1st
            </div>
          </div>
          <Link
            to="/upgrade"
            data-testid="upgrade-ocr-btn"
            className="nb-btn nb-btn-dark !py-1.5 !px-2.5 !text-xs shrink-0"
          >
            <Crown size={12} strokeWidth={3} /> Unlimited
          </Link>
        </div>
      )}

      <div className="nb-card-lg p-6 text-center relative overflow-hidden">
        <div className="mx-auto w-full aspect-[3/4] max-w-xs bg-[#f4f0eb] border-2 border-dashed border-[#1a1a1a] rounded-2xl relative overflow-hidden flex items-center justify-center">
          {preview ? (
            <>
              <img
                src={preview}
                alt="receipt"
                className="w-full h-full object-contain"
                data-testid="receipt-preview"
              />
              {busy && <div className="scan-beam" />}
              {busy && (
                <div className="absolute inset-0 bg-white/40 flex items-end justify-center pb-4">
                  <span className="nb-tag bg-white">Scanning…</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 text-[#4a4a4a] px-6">
              <Camera size={48} strokeWidth={2.5} />
              <div className="font-bold">Point your camera</div>
              <div className="text-xs">
                Snap a clear photo of the whole receipt. We&apos;ll extract the payee, amount and date.
              </div>
            </div>
          )}
        </div>

        <input
          ref={fileRef}
          data-testid="receipt-file"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFile}
          className="hidden"
        />

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            data-testid="open-camera-btn"
            onClick={() => {
              fileRef.current?.setAttribute("capture", "environment");
              fileRef.current?.click();
            }}
            disabled={busy}
            className="nb-btn nb-btn-dark !py-3"
          >
            <Camera size={18} strokeWidth={3} /> Camera
          </button>
          <button
            data-testid="upload-btn"
            onClick={() => {
              fileRef.current?.removeAttribute("capture");
              fileRef.current?.click();
            }}
            disabled={busy}
            className="nb-btn !py-3"
          >
            <Upload size={18} strokeWidth={3} /> Upload
          </button>
        </div>

        <div className="mt-4 text-xs text-[#4a4a4a] font-medium flex items-center justify-center gap-1">
          <Sparkles size={14} strokeWidth={3} /> Powered by GPT-5.2 vision
        </div>
      </div>

      <Link
        to="/add"
        data-testid="manual-entry-link"
        className="nb-btn nb-btn-secondary w-full"
      >
        Enter manually instead
      </Link>
    </div>
  );
}
